import React, { FC } from 'react';
import { Helmet } from 'react-helmet';
import { t } from '@lingui/macro';

import openGraphRoverLogo from '@rover/react-lib/src/images/logo@12x.png';
import twitterRoverLogo from '@rover/react-lib/src/images/logo-sq@12x.png';
import getCanonicalUrl from '@rover/rsdk/src/modules/Network/getCanonicalUrl';
import avertaRegularBasicWoff2 from '@rover/shared/fonts/Averta-Regular-Basic.woff2';
import useFrontendConfigurationShim from '@rover/shared/js/hooks/useFrontendConfigurationShim';
import useHrefLangsShim from '@rover/shared/js/hooks/useHrefLangsShim';
import avertaFontFaceStyles from '@rover/shared/js/ssr/BaseTemplate/avertaFontFaceStyles';
import bogartFontFaceStyles from '@rover/shared/js/ssr/BaseTemplate/bogartFontFaceStyles';
import catInAFlatFontFaceStyles from '@rover/shared/js/ssr/BaseTemplate/catInAFlatFontFaceStyles';

const HtmlHeader: FC = () => {
  const { hrefLangs } = useHrefLangsShim();
  const { language = 'en-us', settings } = useFrontendConfigurationShim();
  const canonicalUrl = getCanonicalUrl();

  const csrfCookieName = settings?.csrfCookieName;

  return (
    <>
      <Helmet htmlAttributes={{ lang: language, 'xml:lang': language }}>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta
          name="description"
          content={t`Find 5 star dog sitters on Rover.com. Book dog sitting or dog boarding online! 24/7 customer support, peace of mind protection. Better than a dog kennel.`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={t`Rover.com`} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={openGraphRoverLogo} />
        <meta name="twitter:image" content={twitterRoverLogo} />
        <meta name="twitter:card" content="summary" />
        <meta property="fb:admins" content="100002961817747" />
        <meta property="fb:admins" content="552307640" />
        <meta property="fb:admins" content="683595805" />
        <meta property="fb:admins" content="660745755" />
        <meta property="fb:admins" content="1399787495" />
        <meta property="fb:pages" content="199712676794499" />
        <meta name="twitter:site:id" content="315358959" />
        <meta name="inmobi-site-verification" content="2da3b3a1c67bbe07986f764acef942ef" />
        {csrfCookieName && <meta name="csrf-cookie-name" content={csrfCookieName} />}
        <meta property="al:ios:url" content="rover://" />
        <meta property="al:ios:app_store_id" content="547320928" />
        <meta property="al:ios:app_name" content={t`Rover - Dog Lovers for Hire`} />

        <link rel="canonical" href={canonicalUrl} />
        {hrefLangs?.map(({ href, lang }) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={href} />
        ))}

        <link
          rel="icon"
          type="image/x-icon"
          sizes="16x16 24x24 32x32 48x48 64x64 128x128"
          href="/favicon.ico"
        />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16.png" />
        <link rel="icon" type="image/png" sizes="24x24" href="/favicon-24.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32.png" />
        <link rel="icon" type="image/png" sizes="48x48" href="/favicon-48.png" />
        <link rel="icon" type="image/png" sizes="64x64" href="/favicon-64.png" />
        <link rel="icon" type="image/png" sizes="128x128" href="/favicon-128.png" />
        <link rel="icon" type="image/png" sizes="192x192" href="/favicon-192.png" />
        <link rel="icon" type="image/svg+xml" sizes="any" href="/favicon.svg" />

        <link rel="apple-touch-icon" type="image/png" sizes="192x192" href="/favicon-192.png" />

        <link rel="preload" as="font" crossOrigin="anonymous" href={avertaRegularBasicWoff2} />
        <style>{avertaFontFaceStyles}</style>
        <style>{bogartFontFaceStyles}</style>
        <style>{catInAFlatFontFaceStyles}</style>

        {/* Preconnect to required third-party scripts to improve performance https://developers.google.com/web/fundamentals/performance/resource-prioritization#preconnect */}
        <link rel="preconnect" href="http://rover.sjv.io" />
        <link rel="preconnect" href="https://connect.facebook.net" />
        <link rel="preconnect" href="https://inputs.alooma.com" />
      </Helmet>
      <Helmet>
        <link rel="preconnect" href="https://googleads.g.doubleclick.net" />
        <link rel="preconnect" href="https://www.google-analytics.com" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
      </Helmet>
    </>
  );
};

export default HtmlHeader;
